"use client";

// Windowed rows for long task lists and tables (WORK-PERF): past `WINDOW_AFTER` rows, only the
// rows near the viewport are mounted and two spacers stand in for the rest. Work's rows have one
// height (36 px, 44 on coarse pointers), so the window is arithmetic over the measured height of
// a mounted row; no per-row measuring (chat's windowed-rows.ts does that for messages).
//
// The list scrolls with the page's main region, not inside itself, so the window follows any
// scroll (captured at the window) and resizes, read once per animation frame. A row that takes
// the focus is always inside the window (keyboard walking scrolls it into view first, and the
// overscan keeps the next rows mounted).
import * as React from "react";

/** Lists longer than this render a window of rows. */
export const WINDOW_AFTER = 150;

export type RowWindow = { start: number; end: number };

/**
 * The rows of a list whose first row's top is `top` (viewport px) that overlap the viewport
 * `[0, viewport]` widened by `overscan` rows each way. Pure.
 */
export function windowFor(
  top: number,
  viewport: number,
  count: number,
  rowHeight: number,
  overscan: number,
): RowWindow {
  if (count <= 0 || rowHeight <= 0) return { start: 0, end: 0 };
  const first = Math.floor(-top / rowHeight) - overscan;
  const last = Math.ceil((viewport - top) / rowHeight) + overscan;
  const start = Math.min(Math.max(0, first), count);
  const end = Math.max(start, Math.min(count, last));
  return { start, end };
}

/**
 * The window of `count` rows inside the element `ref` points at (the rows' container), or every
 * row while `enabled` is false. Rows carry `data-window-row`; the first mounted one gives the
 * height. `estimate` is the height until then.
 */
export function useRowWindow(
  ref: React.RefObject<HTMLElement | null>,
  count: number,
  o: { enabled: boolean; estimate?: number; overscan?: number },
): RowWindow & { before: number; after: number } {
  const estimate = o.estimate ?? 36;
  const overscan = o.overscan ?? 12;
  const [state, setState] = React.useState<RowWindow & { h: number }>(() => ({
    start: 0,
    end: Math.min(count, 60),
    h: estimate,
  }));
  React.useLayoutEffect(() => {
    if (!o.enabled) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      const el = ref.current;
      if (!el) return;
      const row = el.querySelector<HTMLElement>("[data-window-row]");
      const h = row?.offsetHeight || estimate;
      const w = windowFor(el.getBoundingClientRect().top, window.innerHeight, count, h, overscan);
      setState((s) => (s.start === w.start && s.end === w.end && s.h === h ? s : { ...w, h }));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    window.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
    };
  }, [ref, count, o.enabled, estimate, overscan]);
  if (!o.enabled) return { start: 0, end: count, before: 0, after: 0 };
  const start = Math.min(state.start, count);
  const end = Math.min(Math.max(state.end, start), count);
  return { start, end, before: start * state.h, after: (count - end) * state.h };
}
